import React, { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNotification, InputField } from "@mmfestival/shared-components";
import {
  getFirestore,
  collection,
  query,
  getDocs,
  deleteDoc,
  orderBy,
  limit,
  startAfter,
  doc,
  updateDoc,
  writeBatch,
  getDoc,
  where, // Added where
} from "firebase/firestore";
import axios from "axios"; // Added axios
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEye,
    faEdit,
    faTrash,
    faSearch,
    faEnvelope,
    faDownload,
    faChevronDown,
    faSortUp,
    faSortDown,
    faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { LuActivity, LuCalendar, LuUsers } from "react-icons/lu"; // Added react-icons

import Papa from "papaparse";

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:3000"
    : "");

if (!API_BASE) {
  throw new Error("VITE_API_BASE_URL is missing");
}

// 0 is a real amount (free entries), so only null/undefined/"" become "-".
const formatRupiah = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  return isNaN(n) ? "-" : `Rp ${n.toLocaleString("id-ID")}`;
};

// Midtrans never stores a bank name: bank_transfer carries `va_bank_first`
// ("bni"), while echannel only carries a biller code (70012 = Mandiri). Both
// are resolved here so the desk sees an actual bank rather than a code.
const BILLER_BANKS = { 70012: "Mandiri" };
const BANK_LABELS = {
  bni: "BNI",
  bri: "BRI",
  bca: "BCA",
  cimb: "CIMB Niaga",
  permata: "Permata",
  mandiri: "Mandiri",
};
const getBankName = (p) => {
  if (!p) return "-";
  const va = p.va_bank_first || p.va_numbers?.[0]?.bank;
  if (va) return BANK_LABELS[String(va).toLowerCase()] || String(va).toUpperCase();
  if (p.biller_code) return BILLER_BANKS[p.biller_code] || `Biller ${p.biller_code}`;
  if (p.payment_type === "paypal" || p.paymentMethod === "paypal") return "PayPal";
  return "-";
};

// Midtrans writes plain strings ("2026-03-10 09:26:29"), PayPal writes a
// Firestore Timestamp, and some rows carry an ISO string - normalise all three.
const formatTrxTime = (t) => {
  if (!t) return "-";
  try {
    if (typeof t === "string") {
      const d = new Date(t.includes("T") ? t : t.replace(" ", "T"));
      return isNaN(d.getTime()) ? t : d.toLocaleString("id-ID");
    }
    if (typeof t.toDate === "function") return t.toDate().toLocaleString("id-ID");
    if (t.seconds || t._seconds)
      return new Date((t.seconds ?? t._seconds) * 1000).toLocaleString("id-ID");
    return "-";
  } catch {
    return "-";
  }
};

const getAuthHeaders = async () => {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error("Not authenticated");
  const token = await auth.currentUser.getIdToken();
  return { Authorization: `Bearer ${token}` };
};

// Konstanta untuk pagination
const PAGE_SIZE = 50;

// Komponen untuk baris skeleton saat loading
const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    {Array(9) // Diperbarui menjadi 9 karena kolom grupPengkot ditambahkan
      .fill()
      .map((_, index) => (
        <td key={index} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
  </tr>
));

// Stats Card Component (Copied from Dashboard for consistency)
const StatsCard = ({ title, value, icon, trend, color, subValue }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 transition-all hover:bg-slate-50">
     <div className="flex justify-between items-start mb-4">
        <div>
           <p className="text-slate-500 text-sm font-medium mb-1">{title}</p>
           <h3 className="text-2xl font-bold text-slate-800">{value}</h3>
        </div>
        <div className={`p-2 rounded-lg text-white ${color}`}>
           {icon}
        </div>
     </div>
     {trend && (
        <div className="flex items-center gap-2">
           <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
              {trend}
           </span>
        </div>
     )}
     {subValue && (
        <p className="text-xs text-slate-400 mt-2">{subValue}</p>
     )}
  </div>
);

export const Participants = () => {
  const { user } = useAuth();
  const { showNotification, showConfirm } = useNotification();
  const [state, setState] = useState({
    participants: [],
    filteredParticipants: [],
    searchQuery: "",
    selectedParticipants: [],
    loading: true,
    loadingMore: false,
    lastVisible: null,
    hasMore: true,
    totalParticipants: 0,
    totalFiltered: 0,
    sortKategoriDirection: null,
    allParticipants: [],
    emailTemplateId: 3, // Default template ID untuk Akomodasi Grand Aston
    events: [],
    selectedEvent: "", // Initialize with empty string
    selectedCategory: "", // New state for category filter
    loadingEvents: true,
  });
  const [viewParticipant, setViewParticipant] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvSuccessCount, setCsvSuccessCount] = useState(0);
  const [isProcessingCsv, setIsProcessingCsv] = useState(false);
  const [csvFileName, setCsvFileName] = useState("");
  const [skipNameValidation, setSkipNameValidation] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const db = getFirestore();
  const navigate = useNavigate();
  const isMounted = useRef(true);

  // Cleanup effect
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch Events from semuaKegiatan
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsRef = collection(db, "semuaKegiatan");
        const snapshot = await getDocs(eventsRef);
        const eventsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            events: eventsList,
            selectedEvent: eventsList.length > 0 ? eventsList[0].id : "",
            loadingEvents: false,
          }));
        }
      } catch (err) {
        console.error("Failed to fetch events:", err);
        showNotification("error", "Gagal memuat daftar event.");
        if (isMounted.current) {
          setState((prev) => ({ ...prev, loadingEvents: false }));
        }
      }
    };
    fetchEvents();
  }, [db, showNotification]);

  // Validasi format BIB Number
  const validateBibNumber = (bibNumber) => {
    if (bibNumber === "" || bibNumber === null || bibNumber === undefined) {
      return true;
    }
    return /^\d{1,4}$/.test(bibNumber);
  };

  // Normalisasi string
  const normalizeString = (str) => {
    return str ? str.trim().replace(/\s+/g, " ").toLowerCase() : "";
  };

  // Handler untuk unggah file CSV
  const handleCsvUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFileName(file.name);
    setCsvErrors([]);
    setCsvSuccessCount(0);
    setCsvData([]);
    setSkipNameValidation(false);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const data = result.data;
        if (!data.length) {
          showNotification("error", "CSV kosong atau tidak valid.");
          return;
        }

        const requiredHeaders = [
          "ORDER ID",
          "NAMA LENGKAP",
          "BIB NUMBER"
        ];
        const headers = Object.keys(data[0]).map(h => h.trim().toUpperCase()); // Normalize headers
        const missingHeaders = requiredHeaders.filter(
          (header) => !headers.includes(header)
        );
        
        if (missingHeaders.length > 0) {
           // Fallback for case sensitivity check
           showNotification(
            "error",
            `Header CSV tidak lengkap. Harus menyertakan: ORDER ID, NAMA LENGKAP, BIB NUMBER`
          );
          return;
        }

        setCsvData(data);
        setIsCsvModalOpen(true);
      },
      error: (err) => {
        console.error("[ERROR] Error parsing CSV:", err);
        showNotification(
          "error",
          "Gagal memproses file CSV. Pastikan formatnya benar."
        );
      },
    });
  };

  const handleProcessCsv = async () => {
    if (!csvData.length) {
      showNotification("error", "Tidak ada data CSV untuk diproses.");
      return;
    }

    setIsProcessingCsv(true);
    setCsvErrors([]);
    setCsvSuccessCount(0);
    const newErrors = [];
    const updates = [];

    for (const row of csvData) {
      // Normalize keys to handle case sensitivity
      const normalizedRow = {};
      Object.keys(row).forEach(key => {
        normalizedRow[key.trim().toUpperCase()] = row[key];
      });

      const orderId = normalizedRow["ORDER ID"];
      const namaLengkap = normalizedRow["NAMA LENGKAP"];
      const bibNumber = normalizedRow["BIB NUMBER"];

      if (!orderId || typeof orderId !== "string") {
        newErrors.push(
          `Baris ${csvData.indexOf(row) + 2}: ORDER ID tidak valid.`
        );
        continue;
      }
      // Removed strictly string check for name as it might be empty? No, name is usually required.
      if (!namaLengkap || typeof namaLengkap !== "string") {
        newErrors.push(
          `Baris ${csvData.indexOf(row) + 2}: NAMA LENGKAP tidak valid.`
        );
        continue;
      }
      if (!validateBibNumber(bibNumber)) {
        newErrors.push(
          `Baris ${
            csvData.indexOf(row) + 2
          }: BIB NUMBER harus 1-4 digit angka atau kosong.`
        );
        continue;
      }

      const participantRef = doc(db, `pendaftaran${state.selectedEvent}`, orderId);
      const participantDoc = await getDoc(participantRef);
      if (!participantDoc.exists()) {
        newErrors.push(
          `Baris ${
            csvData.indexOf(row) + 2
          }: Order ID ${orderId} tidak ditemukan.`
        );
        continue;
      }

      const participantData = participantDoc.data();
      let updateData = null;

      const isGroup =
        participantData.participants &&
        Array.isArray(participantData.participants) &&
        participantData.participants.length > 0;

      if (!isGroup) {
        let nameMatch = true;
        if (!skipNameValidation) {
          const namaLengkapDb = normalizeString(participantData.namaLengkap || "");
          const namaLengkapCsvNorm = normalizeString(namaLengkap);
          
          if (namaLengkapDb !== namaLengkapCsvNorm) {
            newErrors.push(
              `Baris ${
                csvData.indexOf(row) + 2
              }: Nama "${namaLengkap}" tidak cocok dengan "${
                participantData.namaLengkap
              }" untuk Order ID ${orderId}.`
            );
            nameMatch = false;
          }
        }

        if (skipNameValidation || nameMatch) {
          updateData = {
            orderId,
            bibNumber: bibNumber || "",
            isGroup: false,
            ref: participantRef,
            currentBibNumber: participantData.bibNumber || "",
          };
        }
      } else {
        // Group Logic - Match by Full Name
        let participantFound = false;
        const namaLengkapCsvNorm = normalizeString(namaLengkap);
        
        // Check main participant first (sometimes main participant is not in sub-array?) 
        // Logic: Usually group master is also a participant or just payer. 
        // Based on previous logic, we check participants array.

        const participantIndex = participantData.participants.findIndex(
            (p) => normalizeString(p.nama || p.namaLengkap || "") === namaLengkapCsvNorm
        );

        if (participantIndex !== -1) {
             updateData = {
                orderId,
                bibNumber: bibNumber || "",
                isGroup: true,
                ref: participantRef,
                participantIndex,
                currentBibNumber:
                  participantData.participants[participantIndex].bibNumber ||
                  "",
              };
              participantFound = true;
        } else {
             // Fallback: Check if it matches the main account holder if they are participating?
             // Usually group structure has all runners in 'participants'.
        }

        if (!participantFound) {
          newErrors.push(
            `Baris ${
              csvData.indexOf(row) + 2
            }: Tidak ditemukan peserta dengan Nama "${namaLengkap}" di grup ${orderId}.`
          );
        }
      }

      if (updateData) {
        updates.push(updateData);
      }
    }

    const bibNumberMap = new Map();
    for (const update of updates) {
      const { bibNumber, orderId } = update;
      if (bibNumber) {
        if (bibNumberMap.has(bibNumber)) {
          newErrors.push(
            `Baris untuk Order ID ${orderId}: BIB Number ${bibNumber} sudah digunakan di baris lain dalam CSV.`
          );
        } else {
          bibNumberMap.set(bibNumber, orderId);
        }
      }
    }

    if (newErrors.length === 0 && updates.length > 0) {
      try {
        const existingBibNumbers = new Map();
        const allDocs = await getDocs(collection(db, `pendaftaran${state.selectedEvent}`));
        allDocs.forEach((doc) => {
          const data = doc.data();
          if (data.bibNumber) {
            existingBibNumbers.set(data.bibNumber, {
              orderId: doc.id,
              isGroup: false,
            });
          }
          if (data.participants && Array.isArray(data.participants)) {
            data.participants.forEach((p, index) => {
              if (p.bibNumber) {
                existingBibNumbers.set(p.bibNumber, {
                  orderId: doc.id,
                  isGroup: true,
                  participantIndex: index,
                });
              }
            });
          }
        });

        const csvBibNumbers = new Set(
          updates.map((u) => u.bibNumber).filter(Boolean)
        );
        const csvOrderIds = new Set(updates.map((u) => u.orderId));

        for (const update of updates) {
          const {
            bibNumber,
            orderId,
            isGroup,
            participantIndex,
            currentBibNumber,
          } = update;
          if (bibNumber && existingBibNumbers.has(bibNumber)) {
            const existing = existingBibNumbers.get(bibNumber);
            if (
              existing.orderId === orderId &&
              isGroup === existing.isGroup &&
              (!isGroup || participantIndex === existing.participantIndex)
            ) {
              continue;
            }
            if (
              csvBibNumbers.has(bibNumber) &&
              csvOrderIds.has(existing.orderId)
            ) {
              continue;
            }
            newErrors.push(
              `Baris untuk Order ID ${orderId}: BIB Number ${bibNumber} sudah digunakan di ${
                existing.isGroup
                  ? `grup ${existing.orderId}, peserta index ${existing.participantIndex}`
                  : `dokumen ${existing.orderId}`
              }.`
            );
          }
        }

        if (newErrors.length === 0) {
          const bibToOrderId = new Map(
            updates.map((u) => [u.bibNumber, u.orderId])
          );
          for (const update of updates) {
            const { bibNumber, orderId, currentBibNumber } = update;
            if (currentBibNumber && bibToOrderId.has(currentBibNumber)) {
              const targetOrderId = bibToOrderId.get(currentBibNumber);
              if (
                targetOrderId !== orderId &&
                !csvOrderIds.has(targetOrderId)
              ) {
                newErrors.push(
                  `Baris untuk Order ID ${orderId}: Penukaran BIB Number ${bibNumber} dengan ${currentBibNumber} tidak valid karena ${currentBibNumber} digunakan di luar CSV.`
                );
              }
            }
          }
        }
      } catch (err) {
        console.error("[ERROR] Error checking bibNumber uniqueness:", err);
        newErrors.push("Gagal memeriksa keunikan BIB Number di database.");
      }
    }

    if (newErrors.length === 0 && updates.length > 0) {
      const batch = writeBatch(db);
      for (const update of updates) {
        const { ref, bibNumber, isGroup, participantIndex } = update;
        if (isGroup) {
          const docSnapshot = await getDoc(ref);
          const participants = docSnapshot.data().participants;
          participants[participantIndex] = {
            ...participants[participantIndex],
            bibNumber: bibNumber || "",
          };
          batch.update(ref, {
            participants,
            updatedAt: new Date(),
          });
        } else {
          batch.update(ref, {
            bibNumber: bibNumber || "",
            updatedAt: new Date(),
          });
        }
      }

      try {
        await batch.commit();
        setCsvSuccessCount(updates.length);
        setState((prev) => {
          let updatedParticipants = [...prev.participants];
          let updatedFilteredParticipants = [...prev.filteredParticipants];
          let updatedAllParticipants = [...prev.allParticipants];

          updates.forEach(
            ({ orderId, bibNumber, isGroup, participantIndex }) => {
              const updateParticipant = (p) => {
                if (p.id === orderId) {
                  if (isGroup) {
                    const participants = [...p.participants];
                    participants[participantIndex] = {
                      ...participants[participantIndex],
                      bibNumber: bibNumber || "",
                    };
                    return { ...p, participants };
                  }
                  return { ...p, bibNumber: bibNumber || "" };
                }
                return p;
              };

              updatedParticipants = updatedParticipants.map(updateParticipant);
              updatedFilteredParticipants =
                updatedFilteredParticipants.map(updateParticipant);
              updatedAllParticipants =
                updatedAllParticipants.map(updateParticipant);
            }
          );

          return {
            ...prev,
            participants: updatedParticipants,
            filteredParticipants: updatedFilteredParticipants,
            allParticipants: updatedAllParticipants,
          };
        });
        showNotification(
          "success",
          `Berhasil memperbarui ${updates.length} BIB Number.`
        );
        setIsCsvModalOpen(false);
        setCsvData([]);
        setCsvFileName("");
      } catch (err) {
        console.error("[ERROR] Error committing batch:", err);
        newErrors.push("Gagal memperbarui BIB Number di database.");
      }
    }

    setCsvErrors(newErrors);
    setIsProcessingCsv(false);

    if (newErrors.length > 0) {
      showNotification(
        "error",
        `Ada ${newErrors.length} kesalahan. Lihat detail di modal.`
      );
    }
  };

  // Fetch peserta dengan pagination
  const loadParticipants = useCallback(
    async (lastDoc = null, sortDirection = state.sortKategoriDirection) => {
      if (!isMounted.current) return;

      if (!state.selectedEvent) return;

      setState((prev) => ({
        ...prev,
        loading: !lastDoc,
        loadingMore: !!lastDoc,
        participants: lastDoc ? prev.participants : [],
        filteredParticipants: state.searchQuery
          ? prev.filteredParticipants
          : [],
      }));

      try {
        const participantsCollection = collection(db, `pendaftaran${state.selectedEvent}`);
        // STRICT FILTER: Only 'settlement'
        const constraints = [
            where("transaction_status", "==", "settlement"),
            orderBy("namaLengkap")
        ];
        
        if (sortDirection) {
          constraints.push(orderBy("kategori", sortDirection));
        }
        
        const baseQuery = lastDoc
          ? query(
              participantsCollection,
              ...constraints,
              startAfter(lastDoc),
              limit(PAGE_SIZE)
            )
          : query(participantsCollection, ...constraints, limit(PAGE_SIZE));

        const participantsSnapshot = await getDocs(baseQuery);
        const participantsList = participantsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          sentTemplateIds: doc.data().sentTemplateIds || [],
          grupPengkot: doc.data().grupPengkot || "-", // Pastikan grupPengkot selalu ada
        }));

        if (!isMounted.current) return;

        setState((prev) => {
          const updatedParticipants = lastDoc
            ? [...prev.participants, ...participantsList]
            : participantsList;
          return {
            ...prev,
            participants: updatedParticipants,
            filteredParticipants: state.searchQuery
              ? prev.filteredParticipants
              : updatedParticipants,
            lastVisible:
              participantsSnapshot.docs[participantsSnapshot.docs.length - 1] ||
              null,
            loading: false,
            loadingMore: false,
            hasMore: participantsSnapshot.docs.length === PAGE_SIZE,
            totalFiltered: state.searchQuery
              ? prev.totalFiltered
              : prev.totalParticipants,
          };
        });
      } catch (err) {
        console.error("[ERROR] Error fetching participants:", err);
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
            loadingMore: false,
            error: "Gagal memuat data peserta. Silakan coba lagi.",
          }));
          showNotification(
            "error",
            "Gagal memuat data peserta. Silakan coba lagi."
          );
        }
      }
    },
    [state.searchQuery, state.sortKategoriDirection, showNotification, state.selectedEvent]
  );


  // Fetch total jumlah peserta (Strict verified count)
  const fetchTotalParticipants = useCallback(async () => {
    if (!state.selectedEvent) return;
    try {
        // 1. Fetch Refunded Transactions via API (Dynamic Exclusion)
        const refundedOrderIds = new Set();
        try {
            const { data: refundData } = await axios.get(`${API_BASE}/api/export-refunds`);
            if (Array.isArray(refundData)) {
                refundData.forEach((r) => {
                    if (r.orderId) refundedOrderIds.add(r.orderId);
                });
            }
        } catch (error) {
            console.warn("Could not fetch refunded transactions via API.", error);
        }

      const participantsCollection = collection(db, `pendaftaran${state.selectedEvent}`);
      const snapshot = await getDocs(participantsCollection);
      
      let verifiedCount = 0;
      snapshot.forEach((doc) => {
         const p = doc.data();
         // STRICT CHECK: Settlement AND Not Refunded
         if (p.transaction_status === 'settlement' && !refundedOrderIds.has(p.orderId) && !refundedOrderIds.has(doc.id)) {
            verifiedCount++;
         }
      });

      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          totalParticipants: verifiedCount, // ONLY VERIFIED
          totalFiltered: state.searchQuery ? prev.totalFiltered : verifiedCount,
        }));
      }
    } catch (err) {
      console.error("[ERROR] Error fetching total participants:", err);
      if (isMounted.current) {
        showNotification(
          "error",
          "Gagal memuat jumlah peserta. Silakan coba lagi."
        );
      }
    }
  }, [state.searchQuery, showNotification, state.selectedEvent]);


  // Fetch semua peserta untuk pencarian (Strict Verified Only)
  const fetchAllParticipants = useCallback(async () => {
    if (!state.selectedEvent) return;
    try {
        // 1. Fetch Refunded Transactions via API
        const refundedOrderIds = new Set();
        try {
            const { data: refundData } = await axios.get(`${API_BASE}/api/export-refunds`);
            if (Array.isArray(refundData)) {
                refundData.forEach((r) => {
                    if (r.orderId) refundedOrderIds.add(r.orderId);
                });
            }
        } catch (error) { console.warn("Refund fetch failed", error); }

      const participantsCollection = collection(db, `pendaftaran${state.selectedEvent}`);
      const snapshot = await getDocs(participantsCollection);
      
      const allParticipants = [];
      snapshot.forEach((doc) => {
         const p = doc.data();
         // STRICT CHECK
         if (p.transaction_status === 'settlement' && !refundedOrderIds.has(p.orderId) && !refundedOrderIds.has(doc.id)) {
             allParticipants.push({
                id: doc.id,
                ...p,
                sentTemplateIds: p.sentTemplateIds || [],
                grupPengkot: p.grupPengkot || "-",
             });
         }
      });

      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          allParticipants,
          totalParticipants: allParticipants.length,
          totalFiltered: state.searchQuery ? prev.totalFiltered : allParticipants.length,
        }));
      }
    } catch (err) {
      console.error("[ERROR] Error fetching all participants:", err);
      if (isMounted.current) {
        showNotification(
          "error",
          "Gagal memuat semua data peserta. Silakan coba lagi."
        );
      }
    }
  }, [state.searchQuery, showNotification, state.selectedEvent]);

  // Efek untuk load data awal
  useEffect(() => {
    loadParticipants();
    fetchTotalParticipants();
    fetchAllParticipants();
  }, [loadParticipants, fetchTotalParticipants, fetchAllParticipants]);

  // Extract Unique Categories
  const uniqueCategories = React.useMemo(() => {
     if (!state.allParticipants.length) return [];
     const categories = state.allParticipants
        .map(p => p.kategori)
        .filter(k => k && typeof k === 'string' && k.trim() !== "");
     return [...new Set(categories)].sort();
  }, [state.allParticipants]);

  // Filter Search & Category
  useEffect(() => {
    let filtered = state.allParticipants;

    // 1. Filter by Category
    if (state.selectedCategory) {
        filtered = filtered.filter(p => p.kategori === state.selectedCategory);
    }

    // 2. Filter by Search Query
    if (state.searchQuery) {
      const lowerQuery = state.searchQuery.toLowerCase();
      filtered = filtered.filter((p) => {
        return (
          (p.namaLengkap && p.namaLengkap.toLowerCase().includes(lowerQuery)) ||
          (p.email && p.email.toLowerCase().includes(lowerQuery)) ||
          (p.orderId && p.orderId.toLowerCase().includes(lowerQuery)) ||
          (p.bibNumber && p.bibNumber.toString().toLowerCase().includes(lowerQuery)) ||
          (p.kategori && p.kategori.toLowerCase().includes(lowerQuery)) ||
          (p.transaction_status && p.transaction_status.toLowerCase().includes(lowerQuery))
        );
      });
    }

    if (state.searchQuery || state.selectedCategory) {
      setState((prev) => ({
        ...prev,
        filteredParticipants: filtered.slice(0, PAGE_SIZE),
        fullFilteredList: filtered,
        totalFiltered: filtered.length,
        hasMore: filtered.length > PAGE_SIZE,
        lastVisible: null,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        filteredParticipants: prev.participants,
        fullFilteredList: [],
        totalFiltered: prev.totalParticipants,
        hasMore: prev.participants.length < prev.totalParticipants,
      }));
    }
  }, [
    state.searchQuery,
    state.selectedCategory, // Dependency Added
    state.participants,
    state.allParticipants,
    state.totalParticipants,
  ]);

  // Load more peserta
  const loadMore = useCallback(() => {
    if (state.loading || state.loadingMore || !state.hasMore) return;

    if (state.searchQuery) {
      const currentIndex = Math.floor(
        state.filteredParticipants.length / PAGE_SIZE
      );
      const startIndex = currentIndex * PAGE_SIZE;
      const additionalParticipants = state.fullFilteredList.slice(
        startIndex,
        startIndex + PAGE_SIZE
      );
      if (!additionalParticipants.length) {
        setState((prev) => ({ ...prev, hasMore: false }));
        return;
      }
      setState((prev) => ({
        ...prev,
        filteredParticipants: [
          ...prev.filteredParticipants,
          ...additionalParticipants,
        ],
        hasMore: startIndex + PAGE_SIZE < prev.fullFilteredList.length,
      }));
    } else {
      loadParticipants(state.lastVisible);
    }
  }, [
    state.loading,
    state.loadingMore,
    state.hasMore,
    state.searchQuery,
    state.filteredParticipants,
    state.fullFilteredList,
    state.lastVisible,
    loadParticipants,
  ]);

  // Toggle sortir kategori
  const handleKategoriSortToggle = useCallback(() => {
    const newDirection =
      state.sortKategoriDirection === "desc" ? "asc" : "desc";
    setState((prev) => ({
      ...prev,
      sortKategoriDirection: newDirection,
      lastVisible: null,
      hasMore: true,
      participants: [],
      filteredParticipants: [],
    }));
    loadParticipants(null, newDirection);
  }, [state.sortKategoriDirection, loadParticipants]);

  // Seleksi semua peserta
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setState((prev) => ({
        ...prev,
        selectedParticipants: prev.filteredParticipants
          .filter((p) => p.transaction_status === "settlement")
          .map((p) => p.id),
      }));
    } else {
      setState((prev) => ({ ...prev, selectedParticipants: [] }));
    }
  };

  // Seleksi satu peserta
  const handleSelectParticipant = (participantId) => {
    setState((prev) => ({
      ...prev,
      selectedParticipants: prev.selectedParticipants.includes(participantId)
        ? prev.selectedParticipants.filter((id) => id !== participantId)
        : [...prev.selectedParticipants, participantId],
    }));
  };

  // Ekspor ke CSV
  const handleExportCSV = () => {
    if (state.selectedParticipants.length === 0) {
      showNotification(
        "error",
        "Pilih setidaknya satu peserta untuk diekspor."
      );
      return;
    }

    const selectedData = state.allParticipants.filter(
      (p) =>
        state.selectedParticipants.includes(p.id) &&
        p.transaction_status === "settlement"
    );
    const headers = [
      "Order ID",
      "Nama",
      "BIB Number",
      "Kategori",
      "Status Transaksi",
      "Email",
      "Template Email Terkirim",
      "Gender",
      "Kota",
      "Alamat",
      "Provinsi",
      "Negara",
      "Telepon",
      "Nomor Identitas",
      "Tanggal Lahir",
      "Golongan Darah",
      "Kontak Darurat",
      "Nama Kontak Darurat",
      "Hubungan Kontak Darurat",
      "Bib Name",
      "Size Jersey",
      "Harga",
      "Jarak",
      "Grup Pengkot", // Tambahkan Grup Pengkot ke header
    ];

    const rows = selectedData.map((p) => [
      p.orderId || "-",
      p.namaLengkap || "-",
      p.bibNumber || "-",
      p.kategori || "-",
      p.transaction_status || "null",
      p.email || "-",
      p.sentTemplateIds?.length > 0 ? p.sentTemplateIds.join(", ") : "-",
      p.gender || "-",
      p.kota || "-",
      p.alamat || "-",
      p.provinsi || "-",
      p.negara || "-",
      p.telepon || "-",
      p.nomorIdentitas || "-",
      p.tanggalLahir || "-",
      p.golonganDarah || "-",
      p.kontakDarurat || "-",
      p.namaKontakDarurat || "-",
      p.hubunganKontakDarurat || "-",
      p.bibName || "-",
      p.sizeJersey || "-",
      p.harga || 0,
      p.distance || 0,
      p.grupPengkot || "-", // Tambahkan grupPengkot ke data
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((field) =>
            typeof field === "string" && field.includes(",")
              ? `"${field}"`
              : field
          )
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `participants_export_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();
    URL.revokeObjectURL(link.href);

    showNotification("success", "Data peserta berhasil diekspor ke CSV.");
  };

  // Handler untuk aksi peserta
  const handleEdit = (participant) => {
    navigate("/participants/edit", { 
      state: { 
        participant: {
          ...participant,
          collectionName: `pendaftaran${state.selectedEvent}`,
          eventId: state.selectedEvent
        }
      } 
    });
  };

  const handleView = (participant) => {
    setViewParticipant(participant);
  };

  const handleDelete = async (id) => {
    showConfirm(
      "Apakah Anda yakin ingin menghapus peserta ini?",
      async () => {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);
          const isUserAdmin =
            userDoc.exists() && userDoc.data().role === "admin";

          const participantRef = doc(db, "pendaftaranJOTR", id);
          const participantDoc = await getDoc(participantRef);

          if (!participantDoc.exists()) {
            showNotification("error", "Peserta tidak ditemukan.");
            return;
          }

          const participantData = participantDoc.data();
          const isOwner = participantData.uid === user.uid;

          if (!isUserAdmin && !isOwner) {
            showNotification(
              "error",
              "Anda tidak memiliki izin untuk menghapus peserta ini."
            );
            return;
          }

          await deleteDoc(participantRef);
          setState((prev) => ({
            ...prev,
            participants: prev.participants.filter((p) => p.id !== id),
            filteredParticipants: prev.filteredParticipants.filter(
              (p) => p.id !== id
            ),
            allParticipants: prev.allParticipants.filter((p) => p.id !== id),
            selectedParticipants: prev.selectedParticipants.filter(
              (selectedId) => selectedId !== id
            ),
            totalParticipants: prev.totalParticipants - 1,
            totalFiltered: prev.totalFiltered - 1,
          }));
          showNotification("success", "Peserta berhasil dihapus.");
        } catch (err) {
          console.error("[ERROR] Error deleting participant:", err);
          showNotification("error", "Gagal menghapus peserta: " + err.message);
        }
      },
      "Hapus",
      "Batal"
    );
  };

  // Validasi format email
  const isValidEmail = (email) => {
    if (!email || typeof email !== "string") {
      return false;
    }
    const trimmedEmail = email.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  };

  // Fungsi untuk delay (untuk menghindari rate limit)
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Kirim email menggunakan Brevo API (satu per satu)
  const sendEmail = async (e) => {
    e.preventDefault();
    setIsSendingEmail(true);

    try {
      const templateId = state.emailTemplateId || 3;
      const eligibleParticipants = state.allParticipants.filter(
        (p) =>
          state.selectedParticipants.includes(p.id) &&
          p.transaction_status === "settlement" &&
          (!p.sentTemplateIds ||
            !p.sentTemplateIds.includes(templateId.toString()))
      );

      if (eligibleParticipants.length === 0) {
        showNotification(
          "error",
          `Tidak ada peserta yang dipilih dengan status settlement yang belum menerima template ID ${templateId}.`
        );
        setIsSendingEmail(false);
        return;
      }

      const recipients = eligibleParticipants
        .map((p) => ({
          id: p.id,
          email: p.email ? p.email.trim() : "",
          name: p.namaLengkap || "Peserta",
          isValid: isValidEmail(p.email),
        }))
        .filter((p) => p.isValid);

      if (recipients.length === 0) {
        showNotification(
          "error",
          `Tidak ada peserta yang dipilih dengan alamat email yang valid untuk template ID ${templateId}.`
        );
        setIsSendingEmail(false);
        return;
      }

      let totalSent = 0;
      const errors = [];

      for (const recipient of recipients) {
        try {
          const headers = await getAuthHeaders();
          await axios.post(
            `${API_BASE}/api/send-brevo-email`,
            {
              recipients: [{ email: recipient.email, name: recipient.name }],
              subject: "MMFestival Notification",
              templateId: templateId,
            },
            { headers }
          );

          // Update Firestore untuk recipient
          try {
            const participantRef = doc(db, "pendaftaranJOTR", recipient.id);
            const participantDoc = await getDoc(participantRef);
            const currentTemplateIds =
              participantDoc.data().sentTemplateIds || [];
            await updateDoc(participantRef, {
              emailSent: true,
              emailSentAt: new Date(),
              sentTemplateIds: [...currentTemplateIds, templateId.toString()],
            });

            setState((prev) => ({
              ...prev,
              participants: prev.participants.map((p) =>
                p.id === recipient.id
                  ? {
                      ...p,
                      emailSent: true,
                      emailSentAt: new Date(),
                      sentTemplateIds: [
                        ...(p.sentTemplateIds || []),
                        templateId.toString(),
                      ],
                    }
                  : p
              ),
              filteredParticipants: prev.filteredParticipants.map((p) =>
                p.id === recipient.id
                  ? {
                      ...p,
                      emailSent: true,
                      emailSentAt: new Date(),
                      sentTemplateIds: [
                        ...(p.sentTemplateIds || []),
                        templateId.toString(),
                      ],
                    }
                  : p
              ),
              allParticipants: prev.allParticipants.map((p) =>
                p.id === recipient.id
                  ? {
                      ...p,
                      emailSent: true,
                      emailSentAt: new Date(),
                      sentTemplateIds: [
                        ...(p.sentTemplateIds || []),
                        templateId.toString(),
                      ],
                    }
                  : p
              ),
            }));

            totalSent += 1;
            showNotification(
              "info",
              `Berhasil mengirim email ke ${recipient.email}. (${totalSent}/${recipients.length})`
            );

            // Delay 100ms untuk menghindari rate limit
            await delay(100);
          } catch (err) {
            console.error(
              `[ERROR] Failed to update email status for ${recipient.id}:`,
              err
            );
            errors.push(
              `Gagal memperbarui status email untuk ${recipient.email}.`
            );
          }
        } catch (err) {
          console.error(
            `[ERROR] Error sending email to ${recipient.email}:`,
            err
          );
          errors.push(
            `Gagal mengirim email ke ${recipient.email}: ${
              err.message || "Terjadi kesalahan."
            }`
          );
        }
      }

      setIsEmailModalOpen(false);

      if (errors.length === 0) {
        showNotification(
          "success",
          `Email dengan template ID ${templateId} berhasil dikirim ke ${totalSent} penerima.`
        );
      } else {
        showNotification(
          "error",
          `Email dikirim ke ${totalSent} penerima, tetapi ada ${
            errors.length
          } kesalahan: ${errors.join("; ")}`
        );
      }
    } catch (err) {
      console.error("[ERROR] Failed to process email sending:", err);
      showNotification(
        "error",
        `Gagal mengirim email: ${err.message || "Terjadi kesalahan."}`
      );
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Handler pencarian
  const handleSearchChange = (e) => {
    setState((prev) => ({
      ...prev,
      searchQuery: e.target.value,
      hasMore: true,
    }));
  };

  // Teks untuk menampilkan jumlah peserta

  // Teks untuk menampilkan jumlah peserta
  const rangeText = `Menampilkan ${state.filteredParticipants.length} dari ${state.totalFiltered} Peserta Terverifikasi`;


  return (
    <div className="space-y-4 pb-10">
      {/* Header with Dashboard Style */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200 pointer-events-none">
            <LuUsers size={120} />
         </div>
         <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
               <div>
                  <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                     <LuActivity size={14} /> Administration
                  </p>
                   <h1 className="text-2xl md:text-3xl font-bold mb-2 break-words">Participant Management</h1>
                   <p className="text-slate-500 text-sm max-w-xl">
                       Search and manage <strong>JOTR CIANJUR SERIES 2026</strong> participants. 
                   </p>
               </div>
            </div>
         </div>
      </div>

        {/* Global Event & Category Selector */}
        <div className="bg-white p-4 rounded-xl border border-gray-200">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 {/* Event Selector */}
                 <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">Pilih Event</label>
                     {state.loadingEvents ? (
                       <div className="w-full h-10 bg-gray-100 animate-pulse rounded-lg"></div>
                     ) : (
                       <select
                         value={state.selectedEvent || ""}
                         onChange={(e) => {
                          const newEvent = e.target.value;
                          setState((prev) => ({
                            ...prev,
                            selectedEvent: newEvent,
                            selectedCategory: "", // Reset category on event change
                            participants: [],
                            filteredParticipants: [],
                            allParticipants: [],
                            totalParticipants: 0,
                            totalFiltered: 0,
                            loading: true,
                          }));
                        }}
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 transition-all cursor-pointer"
                      >
                         <option value="" disabled> -- Pilih Event -- </option>
                         {state.events.map((event) => (
                           <option key={event.id} value={event.id}>
                             {event.title || event.id}
                           </option>
                         ))}
                       </select>
                     )}
                 </div>

                 {/* Category Selector */}
                 <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">Filter Kategori</label>
                     <select
                        value={state.selectedCategory || ""}
                        onChange={(e) => setState(prev => ({ ...prev, selectedCategory: e.target.value }))}
                        disabled={!state.selectedEvent || state.loading || state.allParticipants.length === 0}
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                        <option value="">Semua Kategori</option>
                        {uniqueCategories.map((cat, idx) => (
                            <option key={idx} value={cat}>{cat}</option>
                        ))}
                     </select>
                 </div>
             </div>
        </div>

        {!state.selectedEvent ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
            <FontAwesomeIcon
              icon={faSearch}
              className="text-4xl mb-4 text-gray-300"
            />
            <p className="text-lg font-medium">
              Silakan pilih event terlebih dahulu
            </p>
          </div>
        ) : (
          <>
            {/* Kontrol Peserta - Responsive Wrap & Dynamic Width */}
        <div className="mb-6 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div className="w-full flex-1">
            <InputField
              name="search"
              type="text"
              value={state.searchQuery}
              onChange={handleSearchChange}
              placeholder="Cari data peserta..."
              aria-label="Cari peserta"
              inputClassName="input w-full p-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-400 bg-white text-slate-800 text-sm shadow-sm"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setIsEmailModalOpen(true)}
              disabled={isSendingEmail}
              className={`px-4 py-2 rounded-xl text-sm sm:text-base flex items-center gap-2 ${
                isSendingEmail
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-slate-800 text-white hover:bg-slate-900"
              }`}
            >
              <FontAwesomeIcon icon={faEnvelope} />
              Kirim Email
            </button>
            <button
              onClick={handleExportCSV}
              disabled={state.selectedParticipants.length === 0}
              className={`px-4 py-2 rounded-xl text-sm sm:text-base flex items-center gap-2 ${
                state.selectedParticipants.length === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-slate-700 text-white hover:bg-slate-800"
              }`}
            >
              <FontAwesomeIcon icon={faDownload} />
              Export to CSV
            </button>
            <label className="px-4 py-2 rounded-xl text-sm sm:text-base flex items-center gap-2 bg-slate-700 text-white hover:bg-slate-800 cursor-pointer">
              <FontAwesomeIcon icon={faUpload} />
              Upload BIB Number
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                className="hidden"
                disabled={state.loading || isProcessingCsv}
              />
            </label>
          </div>
        </div>


        {/* Tabel Peserta */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto mb-8 shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>


                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    type="checkbox"
                    checked={
                      state.selectedParticipants.length ===
                        state.filteredParticipants.filter(
                          (p) => p.transaction_status === "settlement"
                        ).length && state.filteredParticipants.length > 0
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-slate-800 focus:ring-slate-400 border-gray-300 rounded"
                  />
                </th>
                
                {/* COLUMN 1: Peserta (Name + Order ID) */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Peserta
                </th>

                {/* COLUMN 2: Kategori */}
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-slate-700"
                  onClick={handleKategoriSortToggle}
                >
                  <span className="flex items-center">
                    Kategori
                    {state.sortKategoriDirection && (
                      <FontAwesomeIcon
                        icon={
                          state.sortKategoriDirection === "asc"
                            ? faSortUp
                            : faSortDown
                        }
                        className="ml-1 text-gray-500"
                      />
                    )}
                  </span>
                </th>

                 {/* COLUMN 3: BIB */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  BIB
                </th>
                
                 {/* COLUMN 4: Status */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>

                 {/* COLUMN 5: Template Email */}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template Email
                </th>
                
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {state.loading ? (
                <SkeletonRow />
              ) : state.filteredParticipants.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-4 text-center text-sm text-gray-600"
                  >
                    {state.searchQuery
                      ? "Tidak ada peserta yang cocok dengan pencarian."
                      : "Belum ada peserta yang terdaftar."}
                  </td>
                </tr>
              ) : (
                state.filteredParticipants.map((p) => {
                  if (p.transaction_status !== "settlement") return null;
                  return (
                    <tr key={p.id}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={state.selectedParticipants.includes(p.id)}
                          onChange={() => handleSelectParticipant(p.id)}
                          className="h-4 w-4 text-slate-800 focus:ring-slate-400 border-gray-300 rounded"
                        />
                      </td>
                      
                      {/* CELL 1: Peserta (Name + Order ID) - HIERARCHICAL as Requested */}
                      <td className="px-4 py-4 text-sm text-gray-800">
                         <div className="flex flex-col">
                            <span className="font-bold uppercase text-gray-900">
                                {p.namaLengkap ? p.namaLengkap.toUpperCase() : "-"}
                            </span>
                            <span className="text-xs text-gray-500 font-mono mt-0.5">{p.orderId || "-"}</span>
                         </div>
                      </td>

                      {/* CELL 2: Kategori */}
                      <td className="px-4 py-4 text-sm text-gray-600 font-medium">
                         {p.kategori || "-"}
                      </td>

                      {/* CELL 3: BIB */}
                      <td className="px-4 py-4 text-sm text-gray-500 font-medium whitespace-nowrap">
                        {p.bibNumber ? (
                          <span className="bg-slate-100 text-slate-700 px-2 py-1 rounded text-xs font-bold border border-slate-200">
                            {p.bibNumber}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic text-xs">No BIB</span>
                        )}
                      </td>

                      {/* CELL 4: Status */}
                      <td className="px-4 py-4 text-sm whitespace-nowrap">
                        <span className="bg-slate-100 text-slate-800 px-2.5 py-1 rounded-full text-xs font-semibold border border-slate-200">
                          {p.transaction_status || "null"}
                        </span>
                      </td>
                      
                      {/* CELL 5: Template Email (Restored) */}
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {p.sentTemplateIds && p.sentTemplateIds.length > 0 ? (
                           <span className="bg-slate-50 text-slate-700 px-2 py-1 rounded text-xs border border-slate-200">
                              {p.sentTemplateIds.join(", ")}
                           </span>
                        ) : "-"}
                      </td>

                      {/* CELL 6: Aksi */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleView(p)}
                          className="text-slate-600 hover:text-slate-900 mr-3"
                          disabled={state.loading}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button
                          onClick={() => handleEdit(p)}
                          className="text-gray-600 hover:text-gray-900 mr-3"
                          disabled={state.loading}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="text-gray-600 hover:text-gray-900"
                          disabled={state.loading}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

          {/* Load More dan Jumlah Peserta */}
          <div className="flex justify-end items-center px-4 py-2">
            <span className="text-sm text-gray-600 mr-4">{rangeText}</span>
            <button
              onClick={loadMore}
              disabled={state.loading || state.loadingMore || !state.hasMore}
              className={`px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1 ${
                state.loading || state.loadingMore || !state.hasMore
                  ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                  : "bg-slate-800 text-white hover:bg-slate-900"
              }`}
              aria-label={
                state.hasMore
                  ? "Muat lebih banyak peserta"
                  : "Akhir daftar peserta"
              }
            >
              <span>{state.hasMore ? "Lihat lebih" : "Akhir peserta"}</span>
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          </div>
          </>
        )}

        {/* Modal Detail Peserta */}
        {viewParticipant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
            <div className="bg-white rounded-xl border border-gray-300 py-6 px-4 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Detail Peserta
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                <p>
                  <strong>Order ID:</strong> {viewParticipant.orderId || "-"}
                </p>
                <p>
                  <strong>Nama:</strong> {viewParticipant.namaLengkap || "-"}
                </p>
                <p>
                  <strong>BIB Number:</strong>{" "}
                  {viewParticipant.bibNumber || "-"}
                </p>
                <p>
                  <strong>Email:</strong> {viewParticipant.email || "-"}
                </p>
                <p>
                  <strong>Kategori:</strong> {viewParticipant.kategori || "-"}
                </p>
                <p>
                  <strong>Status Transaksi:</strong>{" "}
                  {viewParticipant.transaction_status || "null"}
                </p>
                <p>
                  <strong>Template Email Terkirim:</strong>{" "}
                  {viewParticipant.sentTemplateIds?.length > 0
                    ? viewParticipant.sentTemplateIds.join(", ")
                    : "-"}
                </p>
                <p>
                  <strong>Harga:</strong>{" "}
                  {viewParticipant.harga
                    ? `Rp ${viewParticipant.harga.toLocaleString("id-ID")}`
                    : "-"}
                </p>
                <p>
                  <strong>Jarak:</strong>{" "}
                  {viewParticipant.distance
                    ? `${viewParticipant.distance} KM`
                    : "-"}
                </p>
                <p>
                  <strong>Alamat:</strong> {viewParticipant.alamat || "-"}
                </p>
                <p>
                  <strong>Kota:</strong> {viewParticipant.kota || "-"}
                </p>
                <p>
                  <strong>Provinsi:</strong> {viewParticipant.provinsi || "-"}
                </p>
                <p>
                  <strong>Negara:</strong> {viewParticipant.negara || "-"}
                </p>
                <p>
                  <strong>Telepon:</strong> {viewParticipant.telepon || "-"}
                </p>
                <p>
                  <strong>Nomor Identitas:</strong>{" "}
                  {viewParticipant.nomorIdentitas || "-"}
                </p>
                <p>
                  <strong>Tanggal Lahir:</strong>{" "}
                  {viewParticipant.tanggalLahir || "-"}
                </p>
                <p>
                  <strong>Gender:</strong> {viewParticipant.gender || "-"}
                </p>
                <p>
                  <strong>Golongan Darah:</strong>{" "}
                  {viewParticipant.golonganDarah || "-"}
                </p>
                <p>
                  <strong>Kontak Darurat:</strong>{" "}
                  {viewParticipant.kontakDarurat || "-"}
                </p>
                <p>
                  <strong>Nama Kontak Darurat:</strong>{" "}
                  {viewParticipant.namaKontakDarurat || "-"}
                </p>
                <p>
                  <strong>Hubungan Kontak Darurat:</strong>{" "}
                  {viewParticipant.hubunganKontakDarurat || "-"}
                </p>
                <p>
                  <strong>Bib Name:</strong> {viewParticipant.bibName || "-"}
                </p>
                <p>
                  <strong>Size Jersey:</strong>{" "}
                  {viewParticipant.sizeJersey || "-"}
                </p>
                <p>
                  <strong>Grup Pengkot:</strong>{" "}
                  {viewParticipant.grupPengkot || "-"}
                </p>
              </div>

              {/* Detail transaksi. Field-nya berbeda per metode: bank transfer
                  membawa nomor VA, echannel membawa biller/bill key, PayPal
                  membawa nilai USD, dan entri gratis membawa kode voucher -
                  jadi hanya yang terisi yang ditampilkan. */}
              <h4 className="text-base font-semibold text-gray-800 mt-6 mb-3 pt-4 border-t border-gray-200">
                Detail Transaksi
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                <p>
                  <strong>Metode:</strong>{" "}
                  {viewParticipant.paymentMethod || "-"}
                  {viewParticipant.payment_type
                    ? ` / ${viewParticipant.payment_type}`
                    : ""}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  {viewParticipant.transaction_status || "null"}
                </p>
                <p>
                  <strong>Harga Awal:</strong>{" "}
                  {formatRupiah(viewParticipant.originalPrice)}
                </p>
                <p>
                  <strong>Diskon:</strong>{" "}
                  {formatRupiah(viewParticipant.discountAmount)}
                </p>
                <p>
                  <strong>Dibayar (gross_amount):</strong>{" "}
                  {formatRupiah(viewParticipant.gross_amount)}
                </p>
                {viewParticipant.voucherCode && (
                  <p>
                    <strong>Kode Voucher:</strong>{" "}
                    {viewParticipant.voucherCode}
                  </p>
                )}
                {viewParticipant.paypalCaptureAmount !== undefined && (
                  <p>
                    <strong>PayPal (USD):</strong> $
                    {Number(viewParticipant.paypalCaptureAmount).toFixed(2)}
                  </p>
                )}
                <p>
                  <strong>Bank:</strong> {getBankName(viewParticipant)}
                </p>
                {viewParticipant.va_number_first && (
                  <p>
                    <strong>Nomor VA:</strong>{" "}
                    {viewParticipant.va_number_first}
                  </p>
                )}
                {viewParticipant.biller_code && (
                  <p>
                    <strong>Biller Code:</strong>{" "}
                    {viewParticipant.biller_code}
                  </p>
                )}
                {viewParticipant.bill_key && (
                  <p>
                    <strong>Bill Key:</strong> {viewParticipant.bill_key}
                  </p>
                )}
                <p>
                  <strong>Waktu Transaksi:</strong>{" "}
                  {formatTrxTime(viewParticipant.transaction_time)}
                </p>
                <p>
                  <strong>Waktu Settlement:</strong>{" "}
                  {formatTrxTime(viewParticipant.settlement_time)}
                </p>
                <p>
                  <strong>Kedaluwarsa:</strong>{" "}
                  {formatTrxTime(viewParticipant.expiry_time)}
                </p>
                <p>
                  <strong>Fraud Status:</strong>{" "}
                  {viewParticipant.fraud_status || "-"}
                </p>
              </div>
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => setViewParticipant(null)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400 transition-colors duration-200 w-full max-w-xs"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Kirim Email */}
        {isEmailModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl py-6 px-4 max-w-lg w-full mx-4">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Kirim Email ke Peserta
              </h2>
              <form onSubmit={sendEmail}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Template ID (Brevo)
                  </label>
                  <InputField
                    type="number"
                    value={state.emailTemplateId}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        emailTemplateId: parseInt(e.target.value) || 1,
                      }))
                    }
                    placeholder="Masukkan ID template Brevo"
                    inputClassName="input w-full p-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-slate-400 bg-white text-slate-800 text-sm"
                  />
                </div>
                <div className="mb-4 text-sm text-gray-600">
                  <p>
                    Email akan dikirim ke{" "}
                    {
                      state.allParticipants.filter(
                        (p) =>
                          state.selectedParticipants.includes(p.id) &&
                          p.transaction_status === "settlement" &&
                          isValidEmail(p.email) &&
                          (!p.sentTemplateIds ||
                            !p.sentTemplateIds.includes(
                              state.emailTemplateId.toString()
                            ))
                      ).length
                    }{" "}
                    peserta dengan status <strong>settlement</strong>, email
                    valid, dan belum menerima template ID{" "}
                    {state.emailTemplateId || 1}.
                  </p>
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setIsEmailModalOpen(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400"
                    disabled={isSendingEmail}
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 rounded-xl text-white ${
                      isSendingEmail
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-slate-800 hover:bg-slate-900"
                    }`}
                    disabled={isSendingEmail}
                  >
                    {isSendingEmail ? "Mengirim..." : "Kirim Email"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Upload CSV */}
        {isCsvModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl border border-gray-300 py-6 px-4 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Unggah BIB Number via CSV
              </h3>
              {csvFileName && (
                <p className="text-sm text-gray-600 mb-2">
                  File: {csvFileName}
                </p>
              )}
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={skipNameValidation}
                    onChange={(e) => setSkipNameValidation(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-yellow-800">
                    Lewati validasi nama depan (hanya cocokkan Order ID)
                  </span>
                </label>
                <p className="text-xs text-yellow-700 mt-1">
                  Gunakan ini jika nama depan di CSV berbeda format dengan
                  database.
                </p>
              </div>
              {csvData.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-md font-semibold text-gray-800 mb-2">
                    Pratinjau Data
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse border border-gray-200">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Baris
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Order ID
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            Nama Lengkap
                          </th>
                          <th className="border border-gray-200 px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            BIB Number
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {csvData.slice(0, 10).map((row, index) => {
                           // Normalize keys for display
                           const normalizeKey = (obj, key) => {
                              const foundKey = Object.keys(obj).find(k => k.trim().toUpperCase() === key.toUpperCase());
                              return foundKey ? obj[foundKey] : "-";
                           }
                           
                           return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {index + 2}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {normalizeKey(row, "ORDER ID")}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {normalizeKey(row, "NAMA LENGKAP")}
                            </td>
                            <td className="border border-gray-200 px-4 py-2 text-sm text-gray-600">
                              {normalizeKey(row, "BIB NUMBER")}
                            </td>
                          </tr>
                         )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {csvErrors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                  <h4 className="text-md font-semibold text-red-800 mb-2">
                    Kesalahan ({csvErrors.length})
                  </h4>
                  <ul className="list-disc list-inside text-sm text-red-700">
                    {csvErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              {csvSuccessCount > 0 && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm text-green-700">
                    Berhasil memproses {csvSuccessCount} baris.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => {
                    setIsCsvModalOpen(false);
                    setCsvData([]);
                    setCsvErrors([]);
                    setCsvSuccessCount(0);
                    setCsvFileName("");
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400"
                  disabled={isProcessingCsv}
                >
                  Batal
                </button>
                <button
                  onClick={handleProcessCsv}
                  disabled={isProcessingCsv}
                  className={`px-4 py-2 rounded-xl text-white ${
                    isProcessingCsv
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-slate-800 hover:bg-slate-900"
                  }`}
                >
                  {isProcessingCsv ? "Memproses..." : "Proses CSV"}
                </button>
              </div>
            </div>
          </div>
      )}
    </div>
  );
};
